import React, { useEffect, useState, Component } from 'react';
import { useSearchParams } from 'react-router-dom';
import { confirmPasswordReset, applyActionCode } from 'firebase/auth';
import { auth } from '../../firebase';
import { AlertCircleIcon, CheckCircleIcon, EyeIcon, EyeOffIcon, LockIcon, MailCheckIcon, LoaderIcon } from 'lucide-react';
// MODULE-LEVEL LOG: Proves the file is included in the build
console.log('[ResetPasswordPage] Module loaded — build marker: RPP_2025_03_23_B');
// Error boundary to catch any render crashes
class ActionPageErrorBoundary extends Component<{
  children: React.ReactNode;
}, {
  hasError: boolean;
  error: string;
}> {
  constructor(props: {
    children: React.ReactNode;
  }) {
    super(props);
    this.state = {
      hasError: false,
      error: ''
    };
  }
  static getDerivedStateFromError(error: Error) {
    return {
      hasError: true,
      error: error.message
    };
  }
  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('[ResetPasswordPage] Render crash:', error, errorInfo);
  }
  render() {
    if (this.state.hasError) {
      return <div style={styles.pageWrapper} data-id="element-6054">
          <div style={styles.card} data-id="element-6055">
            <div style={styles.cardInner} data-id="element-6056">
              <h2 style={{
              fontSize: '1.5rem',
              fontWeight: 'bold',
              color: '#111827',
              marginBottom: '1rem'
            }} data-id="element-6057">
                Something went wrong
              </h2>
              <p style={{
              color: '#6B7280',
              marginBottom: '1rem'
            }} data-id="element-6058">
                Error: {this.state.error}
              </p>
              <a href="/forgot-password" style={styles.primaryButton} data-id="element-6059">
                Request New Link
              </a>
            </div>
          </div>
        </div>;
    }
    return this.props.children;
  }
}
// ============================================================
// EMAIL VERIFICATION FLOW
// ============================================================
const EmailVerificationFlow = ({
  oobCode
}: {
  oobCode: string;
}) => {
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [error, setError] = useState('');
  useEffect(() => {
    const verifyEmail = async () => {
      try {
        console.log('[ResetPasswordPage] Verifying email with oobCode...');
        await applyActionCode(auth, oobCode);
        console.log('[ResetPasswordPage] Email verified successfully!');
        // Refresh user if logged in
        if (auth.currentUser) {
          await auth.currentUser.reload();
          await auth.currentUser.getIdToken(true);
          console.log('[ResetPasswordPage] User token refreshed after verification');
        }
        setStatus('success');
      } catch (err: any) {
        console.error('[ResetPasswordPage] Email verification error:', err);
        if (err.code === 'auth/expired-action-code') {
          setError('This verification link has expired. Please request a new one.');
        } else if (err.code === 'auth/invalid-action-code') {
          setError('This verification link is invalid or has already been used.');
        } else {
          setError('Failed to verify email. Please try again.');
        }
        setStatus('error');
      }
    };
    verifyEmail();
  }, [oobCode]);
  // Loading state
  if (status === 'loading') {
    return <div style={styles.pageWrapper} data-id="element-6060">
        <div style={styles.card} data-id="element-6061">
          <div style={styles.cardInner} data-id="element-6062">
            <div style={{
            textAlign: 'center'
          }} data-id="element-6063">
              <div style={{
              ...styles.iconCircle,
              backgroundColor: '#DBEAFE'
            }} data-id="element-6064">
                <LoaderIcon style={{
                width: '2rem',
                height: '2rem',
                color: '#2563EB',
                animation: 'spin 1s linear infinite'
              }} data-id="element-6065" />
              </div>
              <h2 style={styles.heading} data-id="element-6066">Verifying Your Email</h2>
              <p style={styles.subtitle} data-id="element-6067">
                Please wait while we verify your email address...
              </p>
            </div>
          </div>
        </div>
        <style data-id="element-6068">{`{spinnerKeyframes}`}</style>
      </div>;
  }
  // Success state
  if (status === 'success') {
    return <div style={styles.pageWrapper} data-id="element-6069">
        <div style={styles.card} data-id="element-6070">
          <div style={styles.cardInner} data-id="element-6071">
            <div style={{
            textAlign: 'center'
          }} data-id="element-6072">
              <div style={{
              ...styles.iconCircle,
              backgroundColor: '#D1FAE5'
            }} data-id="element-6073">
                <MailCheckIcon style={{
                width: '2rem',
                height: '2rem',
                color: '#059669'
              }} data-id="element-6074" />
              </div>
              <h2 style={styles.heading} data-id="element-6075">Email Verified!</h2>
              <p style={styles.subtitle} data-id="element-6076">
                Your email has been successfully verified.
              </p>
            </div>

            <div style={{
            marginTop: '1.5rem',
            padding: '1rem',
            backgroundColor: '#F0FDF4',
            borderLeft: '4px solid #22C55E',
            color: '#166534',
            display: 'flex',
            alignItems: 'flex-start',
            borderRadius: '0 0.5rem 0.5rem 0'
          }} data-id="element-6077">
              <CheckCircleIcon style={{
              width: '1.25rem',
              height: '1.25rem',
              marginRight: '0.5rem',
              flexShrink: 0,
              marginTop: '2px'
            }} data-id="element-6078" />
              <span data-id="element-6079">
                Your email has been successfully verified. You can now sign in
                to your account.
              </span>
            </div>

            <a href="/teacher-login" style={{
            ...styles.primaryButton,
            marginTop: '1.5rem'
          }} data-id="element-6080">
              Go to Sign In
            </a>

            <div style={styles.footer} data-id="element-6081">
              <p style={{
              color: '#6B7280'
            }} data-id="element-6082">
                Need help?{' '}
                <a href="/" style={styles.link} data-id="element-6083">
                  Return to home
                </a>
              </p>
            </div>
          </div>
        </div>
      </div>;
  }
  // Error state
  return <div style={styles.pageWrapper} data-id="element-6084">
      <div style={styles.card} data-id="element-6085">
        <div style={styles.cardInner} data-id="element-6086">
          <div style={{
          textAlign: 'center'
        }} data-id="element-6087">
            <div style={{
            ...styles.iconCircle,
            backgroundColor: '#FEE2E2'
          }} data-id="element-6088">
              <AlertCircleIcon style={{
              width: '2rem',
              height: '2rem',
              color: '#DC2626'
            }} data-id="element-6089" />
            </div>
            <h2 style={styles.heading} data-id="element-6090">Verification Failed</h2>
            <p style={styles.subtitle} data-id="element-6091">
              We couldn't verify your email address.
            </p>
          </div>

          <div style={{
          marginTop: '1.5rem',
          padding: '1rem',
          backgroundColor: '#FEF2F2',
          borderLeft: '4px solid #EF4444',
          color: '#B91C1C',
          display: 'flex',
          alignItems: 'flex-start',
          borderRadius: '0 0.5rem 0.5rem 0'
        }} data-id="element-6092">
            <AlertCircleIcon style={{
            width: '1.25rem',
            height: '1.25rem',
            marginRight: '0.5rem',
            flexShrink: 0,
            marginTop: '2px'
          }} data-id="element-6093" />
            <p style={{
            margin: 0
          }} data-id="element-6094">
              {error}
            </p>
          </div>

          <a href="/teacher-login" style={{
          ...styles.primaryButton,
          marginTop: '1.5rem'
        }} data-id="element-6095">
            Go to Sign In
          </a>

          <div style={styles.footer} data-id="element-6096">
            <p style={{
            color: '#6B7280'
          }} data-id="element-6097">
              Need a new verification email? Sign in and request one from your
              dashboard.
            </p>
          </div>
        </div>
      </div>
    </div>;
};
// ============================================================
// PASSWORD RESET FLOW
// ============================================================
const PasswordResetFlow = ({
  oobCode
}: {
  oobCode: string;
}) => {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [confirmPasswordVisible, setConfirmPasswordVisible] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState({
    score: 0,
    label: 'Too Short',
    color: '#D1D5DB'
  });
  const [passwordError, setPasswordError] = useState('');
  useEffect(() => {
    setPasswordStrength(calculatePasswordStrength(password));
  }, [password]);
  useEffect(() => {
    if (confirmPassword && password !== confirmPassword) {
      setPasswordError('Passwords do not match');
    } else {
      setPasswordError('');
    }
  }, [password, confirmPassword]);
  const calculatePasswordStrength = (pwd: string) => {
    if (!pwd || pwd.length < 1) return {
      score: 0,
      label: 'Too Short',
      color: '#D1D5DB'
    };
    let score = 0;
    if (pwd.length >= 8) score += 1;
    if (pwd.length >= 12) score += 1;
    if (/[a-z]/.test(pwd)) score += 0.5;
    if (/[A-Z]/.test(pwd)) score += 0.5;
    if (/[0-9]/.test(pwd)) score += 1;
    if (/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]+/.test(pwd)) score += 1;
    score = Math.min(4, score);
    const map = [{
      score: 0,
      label: 'Too Short',
      color: '#D1D5DB'
    }, {
      score: 1,
      label: 'Weak',
      color: '#EF4444'
    }, {
      score: 2,
      label: 'Fair',
      color: '#F97316'
    }, {
      score: 3,
      label: 'Good',
      color: '#EAB308'
    }, {
      score: 4,
      label: 'Strong',
      color: '#22C55E'
    }];
    return map[Math.floor(score)];
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (password.length < 8) {
      setError('Password must be at least 8 characters long');
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    try {
      setIsLoading(true);
      console.log('[ResetPasswordPage] Confirming password reset...');
      await confirmPasswordReset(auth, oobCode, password);
      console.log('[ResetPasswordPage] Password reset successful!');
      setIsSuccess(true);
    } catch (err: any) {
      console.error('[ResetPasswordPage] Error:', err);
      if (err.code === 'auth/expired-action-code') {
        setError('This reset link has expired. Please request a new one.');
      } else if (err.code === 'auth/invalid-action-code') {
        setError('This reset link is invalid or has already been used.');
      } else if (err.code === 'auth/weak-password') {
        setError('Password is too weak. Please use a stronger password.');
      } else {
        setError('Failed to reset password. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };
  // Success state
  if (isSuccess) {
    return <div style={styles.pageWrapper} data-id="element-6098">
        <div style={styles.card} data-id="element-6099">
          <div style={styles.cardInner} data-id="element-6100">
            <div style={{
            textAlign: 'center'
          }} data-id="element-6101">
              <div style={{
              ...styles.iconCircle,
              backgroundColor: '#D1FAE5'
            }} data-id="element-6102">
                <CheckCircleIcon style={{
                width: '2rem',
                height: '2rem',
                color: '#059669'
              }} data-id="element-6103" />
              </div>
              <h2 style={styles.heading} data-id="element-6104">Password Reset!</h2>
              <p style={styles.subtitle} data-id="element-6105">
                Your password has been successfully changed.
              </p>
            </div>

            <div style={{
            marginTop: '1.5rem',
            padding: '1rem',
            backgroundColor: '#F0FDF4',
            borderLeft: '4px solid #22C55E',
            color: '#166534',
            display: 'flex',
            alignItems: 'flex-start',
            borderRadius: '0 0.5rem 0.5rem 0'
          }} data-id="element-6106">
              <CheckCircleIcon style={{
              width: '1.25rem',
              height: '1.25rem',
              marginRight: '0.5rem',
              flexShrink: 0,
              marginTop: '2px'
            }} data-id="element-6107" />
              <span data-id="element-6108">
                Your password has been successfully reset. You can now sign in
                with your new password.
              </span>
            </div>

            <a href="/teacher-login" style={{
            ...styles.primaryButton,
            marginTop: '1.5rem'
          }} data-id="element-6109">
              Sign In with New Password
            </a>
          </div>
        </div>
      </div>;
  }
  // Form state
  return <div style={styles.pageWrapper} data-id="element-6110">
      <div style={styles.card} data-id="element-6111">
        <div style={styles.cardInner} data-id="element-6112">
          <div style={{
          textAlign: 'center',
          marginBottom: '2rem'
        }} data-id="element-6113">
            <div style={{
            ...styles.iconCircle,
            backgroundColor: '#DBEAFE'
          }} data-id="element-6114">
              <LockIcon style={{
              width: '2rem',
              height: '2rem',
              color: '#2563EB'
            }} data-id="element-6115" />
            </div>
            <h2 style={styles.heading} data-id="element-6116">Create New Password</h2>
            <p style={styles.subtitle} data-id="element-6117">Enter your new password below</p>
          </div>

          {/* Error Alert */}
          {error && <div style={{
          marginBottom: '1.5rem',
          padding: '1rem',
          backgroundColor: '#FEF2F2',
          borderLeft: '4px solid #EF4444',
          color: '#B91C1C',
          display: 'flex',
          alignItems: 'flex-start',
          borderRadius: '0 0.5rem 0.5rem 0'
        }} data-id="element-6118">
              <AlertCircleIcon style={{
            width: '1.25rem',
            height: '1.25rem',
            marginRight: '0.5rem',
            flexShrink: 0,
            marginTop: '2px'
          }} data-id="element-6119" />
              <div data-id="element-6120">
                <p style={{
              margin: 0
            }} data-id="element-6121">
                  {error}
                </p>
                {(error.includes('expired') || error.includes('invalid')) && <a href="/forgot-password" style={{
              color: '#991B1B',
              fontWeight: '500',
              fontSize: '0.875rem',
              marginTop: '0.5rem',
              display: 'inline-block',
              textDecoration: 'underline'
            }} data-id="element-6122">
                    Request a new reset link
                  </a>}
              </div>
            </div>}

          <form onSubmit={handleSubmit} data-id="element-6123">
            {/* New Password */}
            <div style={{
            marginBottom: '1.5rem'
          }} data-id="element-6124">
              <label htmlFor="reset-password" style={styles.label} data-id="element-6125">
                New Password{' '}
                <span style={{
                color: '#EF4444'
              }} data-id="element-6126">
                  *
                </span>
              </label>
              <div style={{
              position: 'relative'
            }} data-id="element-6127">
                <input type={passwordVisible ? 'text' : 'password'} id="reset-password" value={password} onChange={e => setPassword(e.target.value)} style={styles.input} placeholder="Create a secure password (min. 8 characters)" required disabled={isLoading} data-id="element-6128" />
                <button type="button" style={styles.eyeButton} onClick={() => setPasswordVisible(!passwordVisible)} disabled={isLoading} data-id="element-6129">
                  {passwordVisible ? <EyeOffIcon style={styles.eyeIcon} data-id="element-6130" /> : <EyeIcon style={styles.eyeIcon} data-id="element-6131" />}
                </button>
              </div>
              {/* Strength bar */}
              <div style={{
              marginTop: '0.5rem'
            }} data-id="element-6132">
                <div style={{
                width: '100%',
                height: '0.5rem',
                backgroundColor: '#E5E7EB',
                borderRadius: '9999px',
                overflow: 'hidden'
              }} data-id="element-6133">
                  <div style={{
                  height: '100%',
                  width: `${passwordStrength.score / 4 * 100}%`,
                  backgroundColor: passwordStrength.color,
                  transition: 'width 0.3s, background-color 0.3s'
                }} data-id="element-6134" />
                </div>
                <div style={{
                marginTop: '0.25rem',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }} data-id="element-6135">
                  <span style={{
                  fontSize: '0.75rem',
                  fontWeight: '500',
                  color: passwordStrength.color
                }} data-id="element-6136">
                    {passwordStrength.label}
                  </span>
                  <span style={{
                  fontSize: '0.75rem',
                  color: '#6B7280'
                }} data-id="element-6137">
                    Use 8+ characters, include a number and symbol
                  </span>
                </div>
              </div>
            </div>

            {/* Confirm Password */}
            <div style={{
            marginBottom: '1.5rem'
          }} data-id="element-6138">
              <label htmlFor="reset-confirm-password" style={styles.label} data-id="element-6139">
                Confirm New Password{' '}
                <span style={{
                color: '#EF4444'
              }} data-id="element-6140">
                  *
                </span>
              </label>
              <div style={{
              position: 'relative'
            }} data-id="element-6141">
                <input type={confirmPasswordVisible ? 'text' : 'password'} id="reset-confirm-password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} style={{
                ...styles.input,
                borderColor: passwordError ? '#EF4444' : '#D1D5DB'
              }} placeholder="Confirm your new password" required disabled={isLoading} data-id="element-6142" />
                <button type="button" style={styles.eyeButton} onClick={() => setConfirmPasswordVisible(!confirmPasswordVisible)} disabled={isLoading} data-id="element-6143">
                  {confirmPasswordVisible ? <EyeOffIcon style={styles.eyeIcon} data-id="element-6144" /> : <EyeIcon style={styles.eyeIcon} data-id="element-6145" />}
                </button>
              </div>
              {passwordError && <p style={{
              marginTop: '0.25rem',
              fontSize: '0.875rem',
              color: '#EF4444'
            }} data-id="element-6146">
                  {passwordError}
                </p>}
            </div>

            {/* Submit */}
            <button type="submit" disabled={isLoading || !!passwordError} style={{
            ...styles.primaryButton,
            backgroundColor: isLoading || passwordError ? '#9CA3AF' : '#2563EB',
            cursor: isLoading || passwordError ? 'not-allowed' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }} data-id="element-6147">
              {isLoading ? <>
                  <svg style={{
                animation: 'spin 1s linear infinite',
                marginRight: '0.75rem',
                width: '1.25rem',
                height: '1.25rem'
              }} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" data-id="element-6148">
                    <circle style={{
                  opacity: 0.25
                }} cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" data-id="element-6149" />
                    <path style={{
                  opacity: 0.75
                }} fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" data-id="element-6150" />
                  </svg>
                  Resetting Password...
                </> : 'Reset Password'}
            </button>
          </form>

          <div style={styles.footer} data-id="element-6151">
            <p style={{
            color: '#6B7280'
          }} data-id="element-6152">
              Remember your password?{' '}
              <a href="/teacher-login" style={styles.link} data-id="element-6153">
                Sign in
              </a>
            </p>
          </div>
        </div>
      </div>
      <style data-id="element-6154">{`{spinnerKeyframes}`}</style>
    </div>;
};
// ============================================================
// INVALID LINK STATE
// ============================================================
const InvalidLinkState = ({
  mode
}: {
  mode: string | null;
}) => {
  return <div style={styles.pageWrapper} data-id="element-6155">
      <div style={styles.card} data-id="element-6156">
        <div style={styles.cardInner} data-id="element-6157">
          <div style={{
          textAlign: 'center'
        }} data-id="element-6158">
            <div style={{
            ...styles.iconCircle,
            backgroundColor: '#FEF3C7'
          }} data-id="element-6159">
              <AlertCircleIcon style={{
              width: '2rem',
              height: '2rem',
              color: '#D97706'
            }} data-id="element-6160" />
            </div>
            <h2 style={styles.heading} data-id="element-6161">Invalid Link</h2>
            <p style={styles.subtitle} data-id="element-6162">
              This link is invalid or missing required parameters.
            </p>
          </div>

          <div style={{
          marginTop: '1.5rem',
          padding: '1rem',
          backgroundColor: '#FFFBEB',
          borderLeft: '4px solid #F59E0B',
          color: '#92400E',
          display: 'flex',
          alignItems: 'flex-start',
          borderRadius: '0 0.5rem 0.5rem 0'
        }} data-id="element-6163">
            <AlertCircleIcon style={{
            width: '1.25rem',
            height: '1.25rem',
            marginRight: '0.5rem',
            flexShrink: 0,
            marginTop: '2px'
          }} data-id="element-6164" />
            <p style={{
            margin: 0
          }} data-id="element-6165">
              {mode ? `Unrecognized action type: "${mode}". The link may be corrupted.` : 'This link is missing required parameters. Please request a new one.'}
            </p>
          </div>

          <a href="/forgot-password" style={{
          ...styles.primaryButton,
          marginTop: '1.5rem'
        }} data-id="element-6166">
            Request New Link
          </a>

          <div style={styles.footer} data-id="element-6167">
            <p style={{
            color: '#6B7280'
          }} data-id="element-6168">
              <a href="/teacher-login" style={styles.link} data-id="element-6169">
                Go to Sign In
              </a>
              {' · '}
              <a href="/" style={styles.link} data-id="element-6170">
                Return to Home
              </a>
            </p>
          </div>
        </div>
      </div>
    </div>;
};
// ============================================================
// MAIN COMPONENT — Routes based on mode param
// ============================================================
const ActionPageRouter = () => {
  const [searchParams] = useSearchParams();
  const oobCode = searchParams.get('oobCode');
  const mode = searchParams.get('mode');
  // DIAGNOSTIC: Log what mode we received
  useEffect(() => {
    console.log('[ResetPasswordPage] Component MOUNTED', {
      mode,
      hasOobCode: !!oobCode,
      url: window.location.href
    });
  }, []);
  // No oobCode = invalid link regardless of mode
  if (!oobCode) {
    return <InvalidLinkState mode={mode} data-id="element-6171" />;
  }
  // Route based on mode
  switch (mode) {
    case 'verifyEmail':
      return <EmailVerificationFlow oobCode={oobCode} data-id="element-6172" />;
    case 'resetPassword':
      return <PasswordResetFlow oobCode={oobCode} data-id="element-6173" />;
    default:
      // Unknown or missing mode
      return <InvalidLinkState mode={mode} data-id="element-6174" />;
  }
};
// Exported component with error boundary wrapper
export const ResetPasswordPage = () => {
  return <ActionPageErrorBoundary data-id="element-6175">
      <ActionPageRouter data-id="element-6176" />
    </ActionPageErrorBoundary>;
};
// ============================================================
// SHARED STYLES
// ============================================================
const spinnerKeyframes = `
  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;
const styles: Record<string, React.CSSProperties> = {
  pageWrapper: {
    minHeight: '100vh',
    background: 'linear-gradient(to right, #0F1626, #1E3A8A, #2C3E50)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '1rem 1rem 3rem 1rem'
  },
  card: {
    maxWidth: '28rem',
    width: '100%',
    backgroundColor: 'white',
    borderRadius: '0.75rem',
    overflow: 'hidden',
    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)'
  },
  cardInner: {
    padding: '2.5rem 2rem'
  },
  iconCircle: {
    margin: '0 auto 1rem auto',
    width: '4rem',
    height: '4rem',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  heading: {
    fontSize: '1.875rem',
    fontWeight: 'bold',
    color: '#111827',
    marginBottom: '0.5rem'
  },
  subtitle: {
    color: '#6B7280',
    margin: 0
  },
  primaryButton: {
    display: 'inline-block',
    width: '100%',
    padding: '0.75rem 1rem',
    backgroundColor: '#2563EB',
    color: 'white',
    fontWeight: '500',
    borderRadius: '0.5rem',
    border: 'none',
    fontSize: '1rem',
    textDecoration: 'none',
    textAlign: 'center' as const,
    boxSizing: 'border-box' as const,
    cursor: 'pointer'
  },
  label: {
    display: 'block',
    fontSize: '0.875rem',
    fontWeight: '500',
    color: '#374151',
    marginBottom: '0.25rem'
  },
  input: {
    width: '100%',
    padding: '0.75rem 2.5rem 0.75rem 1rem',
    border: '1px solid #D1D5DB',
    borderRadius: '0.5rem',
    fontSize: '1rem',
    color: '#111827',
    outline: 'none',
    boxSizing: 'border-box' as const
  },
  eyeButton: {
    position: 'absolute' as const,
    top: '50%',
    right: '0.75rem',
    transform: 'translateY(-50%)',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: '0',
    color: '#6B7280'
  },
  eyeIcon: {
    width: '1.25rem',
    height: '1.25rem'
  },
  link: {
    color: '#2563EB',
    fontWeight: '500',
    textDecoration: 'none'
  },
  footer: {
    marginTop: '2rem',
    textAlign: 'center' as const
  }
};